import React, { forwardRef, useCallback, useEffect, useState, useImperativeHandle, useRef, useMemo } from 'react'

import ContextMenu, { ContextMenuTypes, ContextMenuRef } from 'devextreme-react/context-menu'
import LoadIndicator from 'devextreme-react/load-indicator'
import { ISelectionProps } from 'devextreme-react/data-grid'
import { Template } from 'devextreme-react/core/template'
import Tooltip from 'devextreme-react/tooltip'

import { IxBadge } from 'components/badge'

const selectedContextBadge = (data: any) => {
    return (
        <div style={ { display: 'flex', alignItems: 'center' } }>
            <IxBadge value={ data.nSelected } />
            <span style={{ marginLeft: '5px' }}>SELECTED</span>
        </div>
    )
}

const onItemClick = (e: ContextMenuTypes.ItemClickEvent) => {
    e.itemData?.onItemClick && e.itemData.onItemClick()
}

export const ContextMenuItemComponent = ({ data: itemData }: any) => {
    const iconClass = itemData.icon && 'dx-icon ' + (itemData.icon.split(' ').length === 1 ? `dx-icon-${itemData.icon}` : itemData.icon)
    const iconStyle = itemData.iconColor ? { color: itemData.iconColor } : {}

    return (
        <div id={`ddi-${itemData.id}`} style={ { zIndex: 100 } }>
            { itemData.icon && (<i className={ iconClass } style={ { marginRight: '5px', ...iconStyle } } />) }
            <span className='dx-menu-item-text'>{itemData.text}</span>
            {itemData.items && <i className="dx-icon-spinright dx-icon"></i>}
            {
                itemData.hint && (
                    <Tooltip
                        target={`.ix-grid-context-menu li:has(>div>div>#ddi-${itemData.id})`}
                        showEvent={ { name: 'mouseenter', delay: 500 } }
                        hideEvent='mouseleave'
                    >
                        <div>{itemData.hint}</div>
                    </Tooltip>
                )
            }
        </div>
    )
}

export const ContextMenuLoadingComponent = () => (
    <div style={ { display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center' } }>
        <LoadIndicator width={18} height={18} visible={ true } />
    </div>
)

export type IIxGridContextMenuProps = {
    gridId: string,
    menuItems: any,
    selectionMode: ISelectionProps['mode'],
}

export const IxGridContextMenu = forwardRef(({ menuItems, selectionMode, gridId }: IIxGridContextMenuProps, ref) => {
    const ctxMenu = useRef<ContextMenuRef>(null)

    const [ctxMenuOpen, setCtxMenuOpen] = useState(false)
    const [nSelected, setNSelected] = useState(0)

    useImperativeHandle(ref, () => ({
        show: ({ target, offsetX, offsetY, nSelected }: any) => {
            ctxMenu.current?.instance()?.option('position.offset.x', offsetX);
            ctxMenu.current?.instance()?.option('position.offset.y', offsetY);
            ctxMenu.current?.instance()?.option('target',  `#${gridId} tbody tr[aria-rowindex="${target.closest('tr').getAttribute('aria-rowindex')}"] td[aria-colindex="${target.closest('td').getAttribute('aria-colindex')}"]`)
            setNSelected(nSelected)
            setCtxMenuOpen(true)
        }
    }))

    const onHiding = useCallback(() => {
        ctxMenu.current?.instance()?.option('target', undefined)
    }, [])

    useEffect(() => {
        if (ctxMenuOpen) {
            setCtxMenuOpen(false)
            ctxMenu.current?.instance()?.show()
        }
    }, [ctxMenuOpen])

    const items = useMemo(() => {
        const prepSubItem = (cb: Function, parentId: string) => {
            return (item: any, i: number) => ({
                id: `${parentId}-${i}`,
                text: item.name,
                onItemClick: () => cb && cb({ itemData: item }),
                ...item,
            })
        }

        const prepItem = (parentId='') => {
            return (a: any, i: number) => {
                if (a.widget === 'loading') {
                    return { id: i, text: '', template: 'loading', beginGroup: true }
                } else {
                    let _items: any = undefined
                    const id = parentId ? `${parentId}-${i}` : `${i}`
                    if (a.widget === 'dropdown') {
                        _items = a?.options?.items.map(prepSubItem(a?.options?.onItemClick, id))
                    } else if (a.widget === 'contextmenu') {
                        _items = a?.options?.items.map(prepItem(id))
                    }
                    return {
                        id,
                        text: a.name,
                        icon: a?.options?.icon,
                        onItemClick: a?.options?.onClick,
                        beginGroup: i === 0 || a.beginGroup,
                        disabled: a.widget === 'disabledbtn',
                        hint: a.hint,
                        items: _items,
                    }
                }
            }
        }

        const items = menuItems.map(prepItem())

        if (selectionMode === 'multiple' && nSelected) {
            items.unshift({ visible: true, template: 'selectedContextBadge', nSelected, closeMenuOnClick: false })
        }

        return items
    }, [menuItems, selectionMode, nSelected])

    return (
        <ContextMenu
            ref={ ctxMenu }
            onItemClick={ onItemClick }
            onHiding={ onHiding }
            showEvent=''
            itemComponent={ ContextMenuItemComponent }
            items={ items }
            cssClass='ix-grid-context-menu'
        >
            <Template name='selectedContextBadge' render={ selectedContextBadge } />
            <Template name='loading' render={ ContextMenuLoadingComponent } />
        </ContextMenu>
    )
})
